import React, { useMemo, useState } from 'react';
import { Box, Button, Text } from '@rsos/base-components';
import Select from '@rsos/base-components/Select';
import InputText from '@rsos/components/capstone/base/form/inputs/InputText';
import { DebugPanel } from '@rsos/debug-panel';
import { getRSOSMap } from '@rsos/utils/rsosGlobal';
import {
  CALL_TYPE_OPTIONS,
  DEFAULT_CALL_TYPE,
  CallTypeOption,
} from './callTypeOptions';
import useLocationPicker from './useLocationPicker';
import usePlotLocations from './usePlotLocations';

interface LocationDebugPanelControlsProps {
  phoneNumber: string;
}

const LocationDebugPanelControls: React.FC<LocationDebugPanelControlsProps> = ({
  phoneNumber,
}) => {
  const map = getRSOSMap();
  const [includeSecondaryLocation, setIncludeSecondaryLocation] =
    useState(false);
  const [sendSecondaryLocationFirst, setSendSecondaryLocationFirst] =
    useState(false);
  const [selectedCallType, setSelectedCallType] =
    useState<CallTypeOption>(DEFAULT_CALL_TYPE);
  const selectOptions = useMemo(
    () =>
      CALL_TYPE_OPTIONS.map(option => ({
        value: option,
        label: option.label,
      })),
    [],
  );
  const selectedOption = useMemo(
    () => selectOptions.find(o => o.value === selectedCallType),
    [selectOptions, selectedCallType],
  );
  const location = useLocationPicker(map);
  const {
    startPlotting,
    pausePlotting,
    plotNextLocation,
    isPlotting,
    currentLocation,
  } = usePlotLocations({
    phoneNumber,
    includeSecondaryLocation,
    sendSecondaryLocationFirst,
    location,
    classOfService: selectedCallType.classOfService,
    secondaryCallType: selectedCallType.secondaryCallType,
  });

  if (location) {
    return (
      <Box>
        <Text size="small" color="primaryText">
          <strong>Latitude: </strong> {currentLocation?.latitude}
        </Text>
        <Text size="small" color="primaryText">
          <strong>Longitude: </strong> {currentLocation?.longitude}
        </Text>
        <Text size="normal" color="primaryText">
          <label>
            <input
              type="checkbox"
              checked={includeSecondaryLocation}
              onChange={() =>
                setIncludeSecondaryLocation(!includeSecondaryLocation)
              }
            />
            Use Secondary Location
          </label>
        </Text>
        <div style={{ paddingLeft: '20px' }}>
          <Text size="normal" color="primaryText">
            <label>
              <input
                type="checkbox"
                checked={sendSecondaryLocationFirst}
                disabled={!includeSecondaryLocation}
                onChange={() =>
                  setSendSecondaryLocationFirst(!sendSecondaryLocationFirst)
                }
              />
              Send Secondary Location First
            </label>
          </Text>
          <Text size="normal" color="primaryText">
            <label>ANI/ALI Call Type:</label>
          </Text>
          <Select
            value={selectedOption}
            options={selectOptions}
            isDisabled={!includeSecondaryLocation}
            isSearchable={false}
            isClearable={false}
            menuPlacement="auto"
            onChange={(option: { value: CallTypeOption } | null) => {
              if (option) setSelectedCallType(option.value);
            }}
          />
        </div>
        <Box>
          <Button
            onClick={plotNextLocation}
            title="Plot next location"
            btnType="primary"
          >
            <strong>Plot Next Location</strong>
          </Button>
          {!isPlotting ? (
            <Button onClick={startPlotting} title="Play" btnType="secondary">
              <strong>Play</strong>
            </Button>
          ) : (
            <Button onClick={pausePlotting} title="Pause" btnType="secondary">
              <strong>Pause</strong>
            </Button>
          )}
        </Box>
      </Box>
    );
  }

  return (
    <Text>
      <strong>Click the map.</strong>
    </Text>
  );
};

interface LocationDebugPanelProps {
  onClose?: () => void;
}

const LocationDebugPanel: React.FC<LocationDebugPanelProps> = ({ onClose }) => {
  const [phoneNumber, setPhoneNumber] = useState('15555555555');

  return (
    <DebugPanel title="Plot Call Locations" onClose={onClose}>
      <Text size="small" color="primaryText">
        Click anywhere inside the jurisdiction on the map to select the starting
        location or the next location to plot. Then click either
        &quot;Play&quot; to continuously plot locations, or click &quot;Plot
        Next Location&quot; to plot a single location.
      </Text>
      <Text size="small" color="secondaryText">
        <strong>Location types per click:</strong>
        <ul style={{ paddingLeft: '20px', marginLeft: 0 }}>
          <li>
            Without secondary: every click sends <strong>ELS</strong>
          </li>
          <li>
            With secondary (send first unchecked): 1st{'\u2192'}
            <strong>ELS</strong>, 2nd{'\u2192'}
            <strong>ALI</strong>, 3rd+{'\u2192'}
            <strong>ELS</strong>
          </li>
          <li>
            With secondary (send first checked): 1st{'\u2192'}
            <strong>ALI</strong>, 2nd+{'\u2192'}
            <strong>ELS</strong>
          </li>
        </ul>
      </Text>
      <Text size="normal" color="primaryText">
        <label>
          <InputText
            type="text"
            onChange={event => setPhoneNumber(event.target.value)}
            value={phoneNumber}
          />
        </label>
      </Text>
      <LocationDebugPanelControls phoneNumber={phoneNumber} />
    </DebugPanel>
  );
};

export default LocationDebugPanel;
